import { Injectable, ElementRef } from '@angular/core';
import { filter, first } from 'rxjs/operators';

import { MediaResolved } from 'idt-ecommerce-api/catalog/products/medias/medias.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreDetail } from 'idt-ecommerce-api/stores/stores.models';
import { RenderConfiguration } from 'render/render.config';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';


export interface AmandaOptions {
  isWidthParamEnabled: boolean;
  width?: number | null;
// TODO: Remove Akamai support
  impolicy?: string;
}

export type AkamaiOptions = AmandaOptions;

@Injectable()
export class AssetsSharedService {

  private timestamp?: string;

  constructor(private readonly contextService: ContextService) {
    this.contextService.getStore$()
      .pipe(
        filter((storeInfo: StoreDetail) => !!storeInfo && !!storeInfo.details && !!storeInfo.details.timestampBuildVersion),
        first()
      )
      .subscribe((storeInfo: StoreDetail) => {
        this.timestamp = storeInfo.details.timestampBuildVersion;
      });
  }

  /**
   * Returns the URL for the current asset including Amanda Image Video Manager params if needed
   * @param asset asset url to be used as base
   * @param assetsHostUrl assets host url
   * @param amandaOptions Amanda params
   */
  public getAssetUrl(asset: string | undefined, assetsHostUrl: string, amandaOptions?: AmandaOptions): string | undefined {
    let url = asset ? this.getAbsoluteAssetUrl(asset, assetsHostUrl) : undefined;

    if (url) {
      url = this.addAmandaParamsIfNeeded(url, amandaOptions);
      url = this.addTimestampParamsIfNeeded(url);
    }

    return url;
  }

  /**
   * Returns the width for the DOM element that will contain the image
   * @param elRef Reference to the DOM element
   */
  public getWidth(elRef?: ElementRef): number {
    const width: number = elRef?.nativeElement?.offsetWidth;
    return width;
  }

  /**
   * Returns product image data (as LazyDataInterface). Also includes w Amanda param
   */
  public productImageData(
    media: MediaResolved | undefined,
    width: number,
    config: RenderConfiguration,
    impolicy?: string
  ): LazyDataInterface | undefined {
    let productImageData: LazyDataInterface | undefined;

    if (isDefined(media)) {
      const isWidthParamEnabled = config.amandaImageVideoManagerEnabled;
      const options: AmandaOptions | AkamaiOptions = this.isAmandaURL(media.path)
        ? { isWidthParamEnabled, width }
        : { isWidthParamEnabled, width, impolicy };
      const src = this.getAssetUrl(media.path, config.assetsUrl, options);
      productImageData = { src, width: media.size?.width, height: media.size?.height };
    }

    return productImageData;
  }

  /**
   * Returns true if is a path of HLS video (.m3u8 extension)
   * @param path file path or url
   */
  public isHLSVideo(path: string) {
    return path.endsWith('.m3u8') || path.includes('.m3u8?');
  }

  /**
   * @param {number} ih : image height
   * @param {number} iw : image width
   * @param {number} wh : window height
   * @param {number} ww : window width
   * @param {number} [density] : density window
   * @return {number} Optimum image width
   * @public
   */
  public getOptimumImageWidth(
    ih: number,
    iw: number,
    wh: number,
    ww: number,
    density?: number
  ): number {
    let result: number = 0;

    if (!density) {
      density = window.devicePixelRatio;
    }

    if (ih <= wh || iw <= ww) {
      return Math.ceil(iw);
    }

    if (
      this.calculateSideRelation(ih, wh) <= this.calculateSideRelation(iw, ww)
    ) {
      result = Math.ceil(iw * this.calculateSideRelation(iw, ww) * density);
      return (result <= iw) || (Math.ceil(ih * this.calculateSideRelation(iw, ww) * density) <= ih) ? result : iw;
    }

    result = Math.ceil(iw * this.calculateSideRelation(ih, wh) * density);
    return (result <= iw) || (Math.ceil(ih * this.calculateSideRelation(ih, wh) * density) <= ih) ? result : iw;
  }

  // ---- Private functions ----

  /**
   * @param {number} imageSize : height or width of image
   * @param {number} windowSize : height or width of window
   * @return {number}
   */
  private calculateSideRelation(imageSize: number, windowSize: number): number {
    return windowSize / imageSize;
  }

  private getAbsoluteAssetUrl(asset: string, assetsHostUrl: string): string {
    let fullUrl: string = asset.replace(/\\/g, '/'); // replace Windows backslash for forward slash;

    if (this.isRelativePath(fullUrl)) {
      const separator = assetsHostUrl.endsWith('/') ? '' : '/';
      fullUrl = assetsHostUrl + separator + asset;
    }

    return fullUrl;
  }

  private isRelativePath(path: string) {
    return !path.startsWith('http');
  }

  private isAmandaURL(assetURL: string): boolean {
    return assetURL.includes('/assets/public/');
  }

  private addAmandaParamsIfNeeded(assetUrl: string, options?: AmandaOptions): string {
    if (this.isAmandaURL(assetUrl)) {
      if (!assetUrl.includes('w=') && options) {
        const width = this.getStandardWidth(options.width);
        return assetUrl.includes('?') ? `${assetUrl}&w=${width}` : `${assetUrl}?w=${width}`;
      }

      return assetUrl;
    }
    return this.addAkamaiParamsIfNeeded(assetUrl, options);
  }

  // TODO: Remove support from akamai on 01/10/2024
  private addAkamaiParamsIfNeeded(assetUrl: string, akamaiOptions?: AkamaiOptions): string {
    if (!assetUrl.includes('imwidth=') && akamaiOptions) {
      const width = this.getStandardWidth(akamaiOptions.width);
      assetUrl = assetUrl.includes('?') ? `${assetUrl}&imwidth=${width}` : `${assetUrl}?imwidth=${width}`;
    }
    if (!assetUrl.includes('impolicy=') && !!akamaiOptions?.impolicy) {
      assetUrl = assetUrl.includes('?') ? `${assetUrl}&impolicy=${akamaiOptions.impolicy}` : `${assetUrl}?impolicy=${akamaiOptions.impolicy}`;
    }
    if (!assetUrl.includes('imformat=')) {
      const imformat = this.getImformatValue();
      assetUrl = assetUrl.includes('?') ? `${assetUrl}&imformat=${imformat}` : `${assetUrl}?imformat=${imformat}`;
    }

    return assetUrl;
  }

  private addTimestampParamsIfNeeded(assetUrl: string): string | undefined {
    if (!assetUrl.match(/(\?|&)((ts=)|t=)/) && this.timestamp) {
      assetUrl = assetUrl.includes('?') ? `${assetUrl}&ts=${this.timestamp}` : `${assetUrl}?ts=${this.timestamp}`;
    }

    return assetUrl;
  }

  private getStandardWidth(width?: number | null): number {
    const windowWidthFallback = window.innerWidth;
    const defaultWidthFallback = 800;
    const standardMultiple: number = 100;

    if (!width || width === 0) { // fallbacks
      width = windowWidthFallback;

      if (width <= 0) {
        width = defaultWidthFallback;
      }
    }

    if (width < 768) { // in mobile devices the width is doubled to improve resolution
      width *= 2;
    }

    const normalizedWith = Math.ceil(width / standardMultiple) * standardMultiple;
    const amandaWidths = [35, 200, 375, 400, 563, 750, 850, 1024, 1125, 1280, 1440, 1920, 2400, 2560, 4000];
    const finalWidth = amandaWidths.find(amandaWidth => amandaWidth >= normalizedWith) ?? 4000;
    return finalWidth;
  }

  private getImformatValue(): string {
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      return 'safari';
    }
    if (!!(window as any).chrome) {
      return 'chrome';
    }
    return 'generic';
  }

}
